import { computed, onBeforeMount, ref } from 'vue';
import { useUserStore } from '@/stores/user';
import { useGeneralState } from '@/stores/general';
import { useGameStateStore } from '@/stores/gameState';
import { getUser, loginUser, logoutUser } from '@/services/auth';
import { useStatTakerMachine } from '@/workflow/stateMachine';
import { usePlayFlowState } from '@/stores/playFlow';
import { useQueueManager } from '@/stores/queueManager';
import { TUserRole } from '@/types';
import { storeToRefs } from 'pinia';

export const useHome = () => {
  const userStore = useUserStore();
  const gameStateStore = useGameStateStore();
  const generalState = useGeneralState();
  const playFlowState = usePlayFlowState();
  const queueManager = useQueueManager();
  const { metadata, send, context } = useStatTakerMachine();
  const { inDanger } = storeToRefs(gameStateStore);

  queueManager.init();

  const isAuthenticated = computed(() => userStore.isAuthenticated);
  const currentUser = computed(() => userStore.currentUser);
  const notification = computed(() => gameStateStore.notification);

  const activeFooterButton = ref(null);

  const setActiveButton = (name: string) => {
    activeFooterButton.value = name;
  };

  const loading = computed(() => gameStateStore.loading);

  const resolveSearchParams = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    if (!searchParams) return;

    // set quarter from query param
    const quarter = searchParams.get('currentQuarter');
    if (quarter) {
      playFlowState.setQuarter(quarter);
    }

    const role = searchParams.get('role');
    userStore.setUserRole(role as TUserRole);

    if (searchParams.has('gameId')) {
      metadata.value.onNext({
        send,
        payload: {
          gameId: parseInt(searchParams.get('gameId'), 10),
        } as any,
        playFlowState,
        generalState,
        context: context.value,
      });
      if (role && (role === TUserRole.CLOCK || role === TUserRole.PLAYS || role === TUserRole.ALL)) {
        generalState.startHeartbeat();
      }
    }
  };

  onBeforeMount(async () => {
    const user = await getUser();
    if (!user) {
      await loginUser();
    } else {
      userStore.setCurrentUser(user);
      await generalState.fetchGames();
      resolveSearchParams();
    }
  });

  return {
    activeFooterButton,
    currentUser,
    inDanger,
    isAuthenticated,
    loading,
    logoutUser,
    notification,
    setActiveButton,
    userStore,
  };
};
